import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPublicProject } from "../services/publicApi";
import "../styles/DetailPages.css";
import { BACKEND_URL } from "../util/api";

const ProjectDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadProject = async () => {
            try {
                setLoading(true);
                setError(null);

                const result = await getPublicProject(slug);

                if (result.success) {
                    setProject(result.data);
                } else {
                    setError(
                        result.message || "Unable to load project."
                    );
                }
            } catch (err) {
                console.error("Failed to load project:", err);

                setError(
                    err.message || "Unable to load project."
                );
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            loadProject();
        }
    }, [slug]);

    const getMediaUrl = (media) => {
        if (media?.url) {
            return media.url;
        }

        if (media?.file_path) {
            return `${BACKEND_URL}/storage/${media.file_path}`;
        }

        return "";
    };

    const getMediaType = (media) => {
        if (media?.type) {
            return media.type.toLowerCase();
        }

        const mime = media?.mime_type || "";

        if (mime.startsWith("image/")) {
            return "image";
        }

        if (mime.startsWith("video/")) {
            return "video";
        }

        return "document";
    };

    const formatFileSize = (bytes) => {
        if (!bytes) {
            return "";
        }

        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }

        if (bytes < 1024 * 1024 * 1024) {
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        }

        return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    };

    const getFileName = (media) => {
        return (
            media?.display_name ||
            media?.original_name ||
            media?.file_path?.split("/").pop() ||
            "Project file"
        );
    };

    if (loading) {
        return (
            <main className="project-detail-page">
                <div className="container project-detail-loading">
                    Loading project...
                </div>
            </main>
        );
    }

    if (error || !project) {
        return (
            <main className="project-detail-page">
                <div className="container project-detail-error">

                    <h1>Project Not Found</h1>

                    <p>
                        The project you are looking for
                        could not be found.
                    </p>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => navigate("/")}
                    >
                        Back to Home
                    </button>

                </div>
            </main>
        );
    }

    const media = Array.isArray(project.media)
        ? project.media
        : [];

    const imageMedia = media.filter(
        (item) => getMediaType(item) === "image"
    );

    const videoMedia = media.filter(
        (item) => getMediaType(item) === "video"
    );

    const documentMedia = media.filter(
        (item) => getMediaType(item) === "document"
    );

    return (
        <main className="project-detail-page">

            {/* HERO */}
            <section className="project-detail-hero">

                <div className="container">

                    <button
                        type="button"
                        className="detail-back-button"
                        onClick={() => navigate(-1)}
                    >
                        ← Back &nbsp;
                    </button>

                    <div className="project-detail-category">
                        {project.tag ||
                            project.category ||
                            "PROJECT"}
                    </div>

                    <h1>
                        {project.title}
                    </h1>

                    {project.description && (
                        <p className="project-detail-intro">
                            {project.description}
                        </p>
                    )}

                </div>

            </section>

            {/* MAIN IMAGE */}
            {project.image && (
                <section className="project-detail-image-section">

                    <div
                        className="project-detail-image"
                        style={{
                            position: "relative",
                            backgroundImage: `url(${BACKEND_URL}/storage/${project.image})`,
                        }}
                    />

                </section>
            )}

            {/* PROJECT MEDIA */}
            {media.length > 0 && (
                <section className="project-detail-media-section">

                    <div className="container">

                        <div className="project-media-heading">
                            <span>PROJECT MEDIA</span>
                            <h2>
                                Project Gallery & Resources
                            </h2>
                        </div>

                        {/* IMAGES */}
                        {imageMedia.length > 0 && (
                            <div className="project-media-group">

                                <h3>
                                    Images
                                </h3>

                                <div className="project-media-image-grid">

                                    {imageMedia.map((item) => {
                                        const url =
                                            getMediaUrl(item);

                                        return (
                                            <a
                                                key={item.id}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="project-media-image-card"
                                            >
                                                <img
                                                    src={url}
                                                    alt={
                                                        getFileName(
                                                            item
                                                        )
                                                    }
                                                    loading="lazy"
                                                />

                                                <div className="project-media-image-overlay">
                                                    <span>
                                                        {getFileName(
                                                            item
                                                        )}
                                                    </span>
                                                </div>
                                            </a>
                                        );
                                    })}

                                </div>

                            </div>
                        )}

                        {/* VIDEOS */}
                        {videoMedia.length > 0 && (
                            <div className="project-media-group">

                                <h3>
                                    Videos
                                </h3>

                                <div className="project-media-video-grid">

                                    {videoMedia.map((item) => {
                                        const url =
                                            getMediaUrl(item);

                                        return (
                                            <div
                                                key={item.id}
                                                className="project-media-video-card"
                                            >

                                                <video
                                                    controls
                                                    preload="metadata"
                                                    className="project-media-video"
                                                >
                                                    <source
                                                        src={url}
                                                        type={
                                                            item.mime_type
                                                        }
                                                    />

                                                    Your browser does
                                                    not support video
                                                    playback.
                                                </video>

                                                <div className="project-media-file-info">

                                                    <strong>
                                                        {getFileName(
                                                            item
                                                        )}
                                                    </strong>

                                                    {item.file_size && (
                                                        <small>
                                                            {formatFileSize(
                                                                item.file_size
                                                            )}
                                                        </small>
                                                    )}

                                                </div>

                                            </div>
                                        );
                                    })}

                                </div>

                            </div>
                        )}

                        {/* DOCUMENTS */}
                        {documentMedia.length > 0 && (
                            <div className="project-media-group">

                                <h3>
                                    Documents & Files
                                </h3>

                                <div className="project-media-document-list">

                                    {documentMedia.map((item) => {
                                        const url =
                                            getMediaUrl(item);

                                        return (
                                            <a
                                                key={item.id}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="project-media-document-card"
                                            >

                                                <div className="project-media-document-icon">
                                                    📄
                                                </div>

                                                <div className="project-media-document-info">

                                                    <strong>
                                                        {getFileName(
                                                            item
                                                        )}
                                                    </strong>

                                                    <small>
                                                        {item.mime_type ||
                                                            "Document"}

                                                        {item.file_size
                                                            ? ` • ${formatFileSize(
                                                                item.file_size
                                                            )}`
                                                            : ""}
                                                    </small>

                                                </div>

                                                <div className="project-media-document-action">
                                                    ↗
                                                </div>

                                            </a>
                                        );
                                    })}

                                </div>

                            </div>
                        )}

                    </div>

                </section>
            )}

            {/* CONTENT */}
            <section className="project-detail-content-section">

                <div className="container project-detail-grid">

                    <article className="project-detail-content">

                        {project.content ? (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: project.content,
                                }}
                            />
                        ) : (
                            <p>
                                {project.description}
                            </p>
                        )}

                    </article>

                    {/* SIDEBAR */}
                    <aside className="project-detail-sidebar">

                        {project.location && (
                            <div className="project-detail-meta">

                                <small>
                                    LOCATION
                                </small>

                                <strong>
                                    {project.location}
                                </strong>

                            </div>
                        )}

                        {project.category && (
                            <div className="project-detail-meta">

                                <small>
                                    CATEGORY
                                </small>

                                <strong>
                                    {project.category}
                                </strong>

                            </div>
                        )}

                        {project.created_at && (
                            <div className="project-detail-meta">

                                <small>
                                    DATE
                                </small>

                                <strong>
                                    {new Date(
                                        project.created_at
                                    ).toLocaleDateString(
                                        "en-NG",
                                        {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        }
                                    )}
                                </strong>

                            </div>
                        )}

                        {/* MEDIA COUNT */}
                        {media.length > 0 && (
                            <div className="project-detail-meta">

                                <small>
                                    MEDIA
                                </small>

                                <strong>
                                    {media.length}{" "}
                                    {media.length === 1
                                        ? "file"
                                        : "files"}
                                </strong>

                            </div>
                        )}

                    </aside>

                </div>

            </section>

            {/* BACK TO PROJECTS */}
            <section className="project-detail-footer">

                <div className="container">

                    <button
                        type="button"
                        className="text-link"
                        onClick={() => navigate("/projects")}
                    >
                        ← View All Projects
                    </button>

                </div>

            </section>

        </main>
    );
};

export default ProjectDetail;